import { useTranslation } from "react-i18next"

import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import type { CollabOfferedProduct } from "@/types/collab"

/**
 * OKS-990 — the variant chooser, shared by BOTH surfaces that record a pick:
 * the request picker (`DropPickerDialog`) and the R13 void hatch
 * (`VoidPossessionDialog`).
 *
 * # ⚠️ SHARED ON PURPOSE — the label cascade must not fork
 *
 * OKS-987's carve-out is `title ?? sku ?? "unnamed variant"`, never a bare id,
 * and it is the kind of rule that gets half-reimplemented. Two hand-written
 * lists would drift the moment one of them gained a case: a creator would meet
 * "Variante sans nom" on one screen and a raw database id on the other, and
 * each surface's own tests would stay green throughout. One component means the
 * cascade has one definition and one place to fix.
 *
 * # ⚠️ `testId` and `ariaLabel` are per-surface, and that is deliberate
 *
 * The two dialogs are different screens with different anchors and different
 * accessible names — « Choisis ta variante » at the request, the void's own
 * wording at the hatch. Sharing the RENDERING must not force them to share
 * their IDENTITY, or an E2E arm aimed at the void would match the request
 * picker's nodes and pass on the wrong screen.
 */
export function VariantStep({
  product,
  value,
  onChange,
  errorId,
  testId = "drop-picker-variant-option",
  ariaLabel,
}: {
  product: CollabOfferedProduct | null
  value: string
  onChange: (next: string) => void
  errorId?: string
  testId?: string
  ariaLabel?: string
}) {
  const { t } = useTranslation()
  if (!product) return null

  return (
    <RadioGroup
      value={value}
      onValueChange={onChange}
      aria-label={ariaLabel ?? t("collabs.drop_picker.variant_step.title")}
      aria-describedby={errorId}
      className="flex flex-col gap-2"
    >
      {product.variants.map((variant) => {
        const id = `${testId}-${variant.id}`
        // OKS-987's carve-out, unchanged: title preferred, SKU when title-less,
        // and a named fallback when neither exists. Never a bare id.
        const label =
          variant.title ??
          variant.sku ??
          t("collabs.drop_picker.unnamed_variant")

        return (
          <div
            key={variant.id}
            data-testid={testId}
            className="flex min-w-0 items-center gap-3 rounded-md border p-3"
          >
            <RadioGroupItem
              value={String(variant.id)}
              id={id}
              aria-label={label}
              className="shrink-0"
            />
            <Label
              htmlFor={id}
              className="min-w-0 flex-1 cursor-pointer font-normal wrap-anywhere"
            >
              {label}
            </Label>
          </div>
        )
      })}
    </RadioGroup>
  )
}
